import { Flex, Pill, Text, Tooltip } from '@fullstory/components';
import { JustifyContent } from '@fullstory/components/legacy/Flex';
import { intl } from '@fullstory/intl';
import { useElementSize } from '@fullstory/react-hooks';
import { isEqual } from 'lodash';
import React, { useLayoutEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const DEFAULT_MAX_VALUES = {
  limit: 5,
  showMoreMessage: false,
};
const SHOW_MORE_VALUES = '. . .';

interface MaxValuesConfig {
  limit: number;
  showMoreMessage?: boolean;
}

interface SampleValuesCellProps {
  sampleValues: string[];
  maxValues?: MaxValuesConfig;
  emptyMessage?: string;
  showMoreContent?: React.ReactNode; // if supplied, we will only present the show more content (when there are values), no sample value pills
  justifyContent?: JustifyContent;
}

export const SampleValues: React.FC<SampleValuesCellProps> = ({
  sampleValues,
  maxValues = DEFAULT_MAX_VALUES,
  emptyMessage = intl.formatMessage({ defaultMessage: 'No sample values available', id: 'WzgXgS' }),
  showMoreContent,
  ...rest
}) => {
  const { elementSize, ref } = useElementSize();
  const [overflowElement, setOverflowElement] = useState<HTMLElement | null>(null);
  const [limit, setLimit] = useState({ value: 0, max: maxValues.limit, elemWidth: 0 });

  useLayoutEffect(() => {
    if (showMoreContent) {
      // when show more content is explicitly configured, we always just use a 0 limit and default for other values
      setLimit(existingValue => {
        const defaultValue = { value: 0, max: maxValues.limit, elemWidth: elementSize?.width ?? 0 };
        return isEqual(existingValue, defaultValue) ? existingValue : defaultValue;
      });
    } else if (overflowElement && elementSize) {
      if (overflowElement.clientWidth < overflowElement.scrollWidth) {
        setLimit(limit => {
          if (limit.value <= 0) {
            return limit;
          }
          // both the new value and the max value should be set to the new limit
          // we know with the current element size that we shouldn't even try to expand past this lower max
          const newValue = limit.value - 1;
          return { value: newValue, max: newValue, elemWidth: elementSize.width };
        });
      } else {
        setLimit(limit => {
          // if the element size has expanded a bit, we will allow the limit to increase to try and fit more in
          const effectiveMax =
            elementSize.width - limit.elemWidth > 10 ? maxValues.limit : limit.max;
          if (limit.value >= effectiveMax) {
            return limit;
          }
          const newValue = limit.value + 1;
          return { value: newValue, max: effectiveMax, elemWidth: elementSize.width };
        });
      }
    }
  }, [
    limit,
    overflowElement,
    elementSize,
    maxValues.limit,
    sampleValues, // intentionally force layout effect if sample values change as well since element size may not change but content could have
  ]);

  const showMoreTooltipContent = (
    <Flex column gap={4} alignItems="flex-start">
      {sampleValues.slice(limit.value, maxValues.limit).map(value => (
        <Pill key={value} variant="primary" color="default-inverse" noExternalSpacing>
          <Text appearance="bodySmall" color="default-inverse">
            {value ? value : <>&nbsp;</>}
          </Text>
        </Pill>
      ))}
      {sampleValues.length > maxValues.limit && maxValues.showMoreMessage && (
        <Text appearance="bodySmall" color="default-inverse">
          <i>
            <FormattedMessage
              defaultMessage="and {count} more..."
              id="79TiwE"
              values={{ count: sampleValues.length - maxValues.limit }}
            />
          </i>
        </Text>
      )}
    </Flex>
  );

  return (
    <Flex
      data-component="SampleValues"
      ref={elem => {
        ref(elem);
        setOverflowElement(elem);
      }}
      gap={4}
      style={{ overflowX: 'hidden' }}
      {...rest}
    >
      {sampleValues.length === 0 && (
        <Text appearance="bodySmall" color="disabled" noWrap>
          <i>{emptyMessage}</i>
        </Text>
      )}
      {sampleValues.slice(0, limit.value).map(value => (
        <Tooltip key={value} tooltipContent={value} showDelay={300}>
          <Pill variant="primary" noExternalSpacing>
            <Text appearance="bodySmall" noWrap>
              {value ? value : <>&nbsp;</>}
            </Text>
          </Pill>
        </Tooltip>
      ))}
      {limit.value < sampleValues.length && limit.value < maxValues.limit && (
        <Tooltip tooltipContent={showMoreTooltipContent} showDelay={300}>
          {showMoreContent ? (
            showMoreContent
          ) : (
            <Pill variant="primary" noExternalSpacing>
              <Text appearance="bodySmall" noWrap>
                {SHOW_MORE_VALUES}
              </Text>
            </Pill>
          )}
        </Tooltip>
      )}
    </Flex>
  );
};
